import type {
  ICreditPackage,
  ICreditRates,
  ICreditSubscription,
  ICreditsBalance,
} from "../../models/interfaces/credits.interface.ts";
import {PLAN_NAMES} from "./subscriptionPresentation.ts";

interface WidgetCta {
  label: string;
  opens: "plans" | "billing";
}

interface PlanHeader {
  name: string;
  detail: string | null;
  isWarning: boolean;
}

const getMoreCredits: WidgetCta = {label: "Get more credits", opens: "plans"};
const seePlans: WidgetCta = {label: "See plans", opens: "plans"};
export const CTAS = {
  unlimited: null,
  // A pre-core balance says nothing about plans, so it keeps the one action every workspace has.
  legacy: seePlans,
  pastDue: null,
  member: null,
  memberLow: null,
  memberZero: null,
  zero: getMoreCredits,
  low: getMoreCredits,
  cancelling: {label: "Resume plan", opens: "billing"},
  noPlan: seePlans,
  // Until the catalog loads there is no next plan to name.
  upgrade: {label: "Upgrade plan", opens: "plans"},
  current: null,
} satisfies Record<string, WidgetCta | null>;

type WidgetState = keyof typeof CTAS;
// How much is left, whatever the plan or the actions on offer.
type AmountLevel = "normal" | "low" | "zero";
export type MeterTone = "plan" | "warning" | "neutral";

export interface CreditsReading {
  readonly balance: ICreditsBalance;
  readonly subscription: ICreditSubscription | null;
  readonly state: WidgetState;
  readonly amount: AmountLevel;
  // Percent of this month's plan credits used; null when no live plan grants credits to measure against.
  readonly planUsed: number | null;
  // Video the balance covers, with its qualifier: "about 4 h", "about 3 min" or "under 1 min".
  readonly runway: string;
}

// 60 credits is about 10 minutes of subtitled video (transcript + one translation) and the signup gift.
const LOW_FLOOR_CREDITS = 60;
const ADMIN_HINT = "Ask a workspace admin to add credits";
const shortUtcDate = new Intl.DateTimeFormat("en-US", {timeZone: "UTC", month: "short", day: "numeric"});
export const compactCount = new Intl.NumberFormat("en-US", {notation: "compact"});
const formatCredits = (credits: number): string => credits.toLocaleString("en-US");

// Only a live subscription is a current plan; a cancelled or expired row is history.
export const liveSubscription = (balance: ICreditsBalance): ICreditSubscription | null =>
  balance.subscription?.live ? balance.subscription : null;

const isLowBalance = (balance: ICreditsBalance, subscription: ICreditSubscription | null): boolean =>
  balance.balance < Math.max(LOW_FLOOR_CREDITS, subscription ? Math.ceil(balance.planGrantedCredits / 10) : 0);

// A pre-core balance has no live plan, so only the 60-credit floor applies to it.
const resolveAmount = (balance: ICreditsBalance, subscription: ICreditSubscription | null): AmountLevel => {
  if (balance.unlimited) return "normal";
  if (balance.balance <= 0) return "zero";
  return isLowBalance(balance, subscription) ? "low" : "normal";
};

const resolveState = (balance: ICreditsBalance, subscription: ICreditSubscription | null): WidgetState => {
  if (balance.unlimited) return "unlimited";
  // Pre-core balances (schema 1) carry none of the plan or permission fields read below.
  if (balance.schemaVersion !== 2 || !balance.allowedActions) return "legacy";
  if (subscription?.status === "past_due") return "pastDue";
  const isLow = isLowBalance(balance, subscription);
  if (!Object.values(balance.allowedActions).some(Boolean)) {
    if (balance.balance <= 0) return "memberZero";
    return isLow ? "memberLow" : "member";
  }
  if (balance.balance <= 0) return "zero";
  if (isLow) return "low";
  if (subscription?.cancelAtPeriodEnd) return "cancelling";
  if (!subscription) return "noPlan";
  return balance.allowedActions.upgrade ? "upgrade" : "current";
};

// Plan credits are spent first, so the share used moves every month however many extra credits the workspace holds.
const planUsedPercent = (balance: ICreditsBalance, subscription: ICreditSubscription | null): number | null =>
  subscription && !balance.unlimited && balance.planGrantedCredits > 0
    ? Math.min(100, Math.max(0, Math.floor((100 * balance.planConsumedCredits) / balance.planGrantedCredits)))
    : null;

const formatRunway = (credits: number, perVideoMinute: number): string => {
  const minutes = Math.floor(Math.max(0, credits) / perVideoMinute);
  if (minutes < 1) return "under 1 min";
  return minutes < 60 ? `about ${minutes} min` : `about ${formatCredits(Math.round(minutes / 60))} h`;
};

export const readCredits = (balance: ICreditsBalance, rates: ICreditRates): CreditsReading => {
  const subscription = liveSubscription(balance);
  const state = resolveState(balance, subscription);
  // A typical subtitle job is a transcript plus one translation, so each minute of video costs both rates.
  const perVideoMinute = rates.transcriptionPerMinute + rates.firstTranslationPerMinute;
  return {
    balance,
    subscription,
    state,
    amount: resolveAmount(balance, state === "legacy" ? null : subscription),
    // A pre-core balance measures no plan, whatever subscription fields it happens to carry.
    planUsed: state === "legacy" ? null : planUsedPercent(balance, subscription),
    runway: formatRunway(balance.balance, perVideoMinute),
  };
};

// Amber asks for action: the balance is short, whether this user tops it up or asks an admin to.
export const isShort = ({amount}: CreditsReading): boolean => amount !== "normal";

// The next tier is the monthly plan ranked right after the live plan by price. A period keeps its old allowance
// until renewal, so credits cannot identify the plan.
const nextTier = (packages: readonly ICreditPackage[], planId: string): ICreditPackage | undefined => {
  const ladder = packages
    .filter((pkg): pkg is ICreditPackage & {priceUsd: number} => pkg.interval === "month" && pkg.priceUsd !== null)
    .sort((a, b) => a.priceUsd - b.priceUsd);
  const rank = ladder.findIndex((pkg) => pkg.id === planId);
  return rank === -1 ? undefined : ladder[rank + 1];
};

// A legacy balance has no plan to open, so a short one offers credits instead of plans.
export const getCta = (
  {state, amount, subscription}: CreditsReading,
  packages: readonly ICreditPackage[] = [],
): WidgetCta | null => {
  if (state === "legacy" && amount !== "normal") return getMoreCredits;
  const next = state === "upgrade" && subscription ? nextTier(packages, subscription.planId) : undefined;
  return next ? {...CTAS.upgrade, label: `Upgrade to ${next.name}`} : CTAS[state];
};

// The ring has no header, so it also carries the past-due warning; with no plan to measure it stays neutral.
export const getRingTone = (reading: CreditsReading): MeterTone => {
  const {state, planUsed} = reading;
  if (isShort(reading) || state === "pastDue") return "warning";
  return planUsed === null ? "neutral" : "plan";
};

export const getPlanHeader = ({state, balance, subscription}: CreditsReading): PlanHeader | null => {
  // Unlimited is only its headline; the block still opens billing without header text.
  if (state === "legacy" || state === "unlimited") return null;
  if (!subscription) return {name: "No plan", detail: null, isWarning: false};
  const name = balance.planName ?? PLAN_NAMES[subscription.planId];
  if (subscription.status === "past_due") return {name, detail: "Payment failed", isWarning: true};
  const date = shortUtcDate.format(new Date(subscription.periodEnd));
  return {name, detail: subscription.cancelAtPeriodEnd ? `Ends ${date}` : `Renews ${date}`, isWarning: false};
};

export const getLegend = ({state, amount, balance, planUsed, runway}: CreditsReading): readonly string[] => {
  const runningLow = `Running low · ${runway} of video`;
  switch (state) {
    case "unlimited":
      return [];
    case "memberZero":
      return [ADMIN_HINT];
    case "memberLow":
      return [runningLow, ADMIN_HINT];
    case "zero":
    case "low":
    case "legacy":
    case "pastDue":
    case "member":
    case "cancelling":
    case "noPlan":
    case "upgrade":
    case "current":
      if (amount === "zero") return ["Add credits to start new projects"];
      if (amount === "low") return [runningLow];
      if (planUsed === null) return [`${runway.charAt(0).toUpperCase()}${runway.slice(1)} of video · never expire`];
      return balance.nonPlanCredits > 0 ? [`+${formatCredits(balance.nonPlanCredits)} extra · never expire`] : [];
  }
};

export const creditsLeft = (balance: number): string => {
  const left = Math.max(0, balance);
  return `${formatCredits(left)} ${left === 1 ? "credit" : "credits"} left`;
};

// Minimized, the tooltip is the only words the ring has, so it names whatever turned the ring amber.
export const getRingSummary = (reading: CreditsReading, header: PlanHeader | null): string => {
  const {state, amount, balance, planUsed, runway} = reading;
  if (state === "unlimited") return "Unlimited credits";
  const measure = header && planUsed !== null
    ? `${header.name}, ${planUsed}% of plan used`
    : `${runway} of video`;
  const summary = `${creditsLeft(balance.balance)} · ${measure}`;
  if (state === "pastDue") return `Payment failed · ${summary}`;
  return amount === "low" ? `Running low · ${summary}` : summary;
};
